import { isDefined } from '@sgme/fp';

import { getSignalRConnector } from '@/api/signalR/SignalRConnector.ts';
import { webapi, type WebapiEndpointBuilder } from '@/api/webapi.ts';
import type { AppDispatch } from '@/store/store.ts';
import { getRequestSwapRfsMutationCacheKey } from '@/store/swap/selectors/rfs.ts';
import { swapErrorOccurred, swapRequestCanceled, swapRfsQuoteReceived } from '@/store/swap/slice.ts';
import type { ISODate, ISODateTime } from '@/types/date.ts';
import type { CurrencyIndex } from '@/utils/currencies/types.ts';
import { formatFullISO, type Timestamp } from '@/utils/date.ts';
import type { IQuoteAbort, QuoteWithPrice, ReceivedSwapQuoteReply } from '@/utils/rfs/type.ts';

export type SwapRfsRequestParams = {
  type: 'FxSwap' | 'FxRollover' | 'FxPredeliver' | 'FxNdSwap';
  tileId: string;
  rfsId: string;
  requestedAt: Timestamp;
  signalRConnectionId: string;
  counterPartyId: number; // bdrId 20252

  currency1: string;
  currency2: string;
  currencyPairPrecision: number;

  notionalCurrencyIndex: CurrencyIndex;

  nearNotional: number;
  nearExpiryDate: ISODateTime; // ISO date like "2025-04-04T00:00:00.000Z"
  nearPrice?: number;
  nearFixingDate?: ISODate;

  farNotional: number;
  farExpiryDate: ISODateTime; // ISO date like "2025-04-04T00:00:00.000Z"
  farPrice?: number;
  farFixingDate?: ISODate;

  xCurrency?: string;
  fixingCurrency?: string;
  fixingSource?: string;
  sndFixingSource?: string;
};

export type SwapRfsRequestBody = {
  fxSwapProduct: SwapRfsProduct;

  replyToStream: string;
  rfsId: string; // use generateRfsId() to create a new rfs ID
  requesterCounterpartyId: number;
  forceTradable: boolean;
};

export type SwapRfsProduct = {
  swapType: 'FxSwap' | 'FxRollover' | 'FxPredeliver' | 'FxNdSwap';

  currencyPair: string;
  currency1: string;
  currency2: string;

  negociatedCurrency: 1 | 2;
  spotPrecision: number;
  extendedSwapPrecision: boolean;

  // NDS
  xCurrency?: string;
  fixingCurrency?: string;
  fixingSource?: string;
  sndFixingSource?: string;

  nearLeg: SwapLeg;
  nearFixingDate?: ISODate; // NDS

  farLeg: SwapLeg;
  farFixingDate?: ISODate; // NDS
};

export type SwapLeg = {
  negociatedAmount: number;
  valueDate: string; // ISO date "2025-04-17T00:00:00.000Z"
  price?: number | null;
};

export type SwapRfsRequestResponse = {};

export const requestSwapRfs = (builder: WebapiEndpointBuilder) => {
  return builder.mutation<SwapRfsRequestResponse, SwapRfsRequestParams>({
    query: (params) => {
      const body = getSwapRfsBody(params);

      return {
        url: '/fxSwap/rfs/request',
        method: 'POST',
        body,
      };
    },

    async onCacheEntryAdded(params, { cacheDataLoaded, cacheEntryRemoved, dispatch }) {
      // wait for the initial query to resolve before proceeding
      await cacheDataLoaded;
      const handleReceiveQuoteReply = buildHandleReceiveQuoteReply(params.tileId, params.rfsId, dispatch);
      const handleReceiveQuoteWithdraw = buildHandleReceiveQuoteReply(params.tileId, params.rfsId, dispatch, true);
      const handleReceiveQuoteAbort = buildHandleReceiveQuoteAbort(params.tileId, params.rfsId, dispatch);

      const signalRConnector = getSignalRConnector();

      signalRConnector.on('ReceiveQuoteReply', handleReceiveQuoteReply);
      signalRConnector.on('ReceiveQuoteWithdraw', handleReceiveQuoteWithdraw);
      signalRConnector.on('ReceiveQuoteAbort', handleReceiveQuoteAbort);

      await cacheEntryRemoved;

      signalRConnector.off('ReceiveQuoteReply', handleReceiveQuoteReply);
      signalRConnector.off('ReceiveQuoteWithdraw', handleReceiveQuoteWithdraw);
      signalRConnector.off('ReceiveQuoteAbort', handleReceiveQuoteAbort);
    },
  });
};

export function closeSwapRfs(tileId: string, _rfsId: string, dispatch: AppDispatch) {
  dispatch(webapi.internalActions.removeMutationResult({ fixedCacheKey: getRequestSwapRfsMutationCacheKey(tileId) }));
  dispatch(swapRequestCanceled({ tileId }));
}

export function getSwapRfsBody(request: SwapRfsRequestParams) {
  return {
    fxSwapProduct: {
      swapType: request.type,

      currencyPair: `${request.currency1}/${request.currency2}`,
      currency1: request.currency1,
      currency2: request.currency2,

      negociatedCurrency: request.notionalCurrencyIndex,
      spotPrecision: request.currencyPairPrecision,
      // Always true: swap prices are requested with extended precision, in this app and in the
      // legacy fx-web app it was rewritten from (swapQuoteRequestBuilder.ts hardcodes it too).
      // The backend user profile still carries a same-named flag, but no UI ever exposed it
      // (neither app renders a checkbox for it) and neither app has ever read it. It is therefore
      // a request-level constant, not a user preference.
      extendedSwapPrecision: true,

      xCurrency: request.xCurrency,
      fixingCurrency: request.fixingCurrency,
      fixingSource: request.fixingSource,
      sndFixingSource: request.sndFixingSource,

      nearLeg: {
        negociatedAmount: request.nearNotional,
        valueDate: formatFullISO(request.nearExpiryDate), // ISO date "2025-04-17T00:00:00.000Z"
        price: request.nearPrice ?? (request.type === 'FxSwap' || request.type === 'FxNdSwap' ? null : undefined),
      },

      nearFixingDate: request.nearFixingDate,

      farLeg: {
        negociatedAmount: request.farNotional,
        valueDate: formatFullISO(request.farExpiryDate), // ISO date "2025-04-17T00:00:00.000Z"
        price: request.farPrice ?? (request.type === 'FxSwap' || request.type === 'FxNdSwap' ? null : undefined),
      },

      farFixingDate: request.farFixingDate,
    },

    replyToStream: request.signalRConnectionId,
    rfsId: request.rfsId,
    requesterCounterpartyId: request.counterPartyId,
    // Swap has no indicative-quote flow (unlike Option/Accumulator tiles), so quotes are always tradable
    forceTradable: true,
  } satisfies SwapRfsRequestBody;
}

// ███████╗██╗ ██████╗ ███╗   ██╗ █████╗ ██╗         ██████╗
// ██╔════╝██║██╔════╝ ████╗  ██║██╔══██╗██║         ██╔══██╗
// ███████╗██║██║  ███╗██╔██╗ ██║███████║██║         ██████╔╝
// ╚════██║██║██║   ██║██║╚██╗██║██╔══██║██║         ██╔══██╗
// ███████║██║╚██████╔╝██║ ╚████║██║  ██║███████╗    ██║  ██║
// ╚══════╝╚═╝ ╚═════╝ ╚═╝  ╚═══╝╚═╝  ╚═╝╚══════╝    ╚═╝  ╚═╝

function buildHandleReceiveQuoteReply(tileId: string, rfsId: string, dispatch: AppDispatch, isWithdraw = false) {
  return (reply: QuoteWithPrice<ReceivedSwapQuoteReply>) => {
    if (reply.SwapQuoteReply?.RfsId !== rfsId) {
      return;
    }

    dispatch(
      swapRfsQuoteReceived({
        tileId,
        requestId: rfsId,
        isWithdraw,
        reply: reply.SwapQuoteReply,
        backendTimestamp: reply.Timestamp,

        // the shared worker stamps workerTimestamp on every RFS message (WorkerLogic.handleRfsMessage),
        // i.e. when the price actually reached the browser -- not when it lands in the front store.
        // It is used to compute the price latency, and to know whether the price is stale when the
        // user executes a trade. Same source as cash, option, flexi-forward, accumulators and bulk.
        frontendTimestamp: reply.workerTimestamp,
      }),
    );
  };
}

function buildHandleReceiveQuoteAbort(tileId: string, rfsId: string, dispatch: AppDispatch) {
  return (reject: IQuoteAbort) => {
    if (reject.RequestId !== rfsId) {
      return;
    }

    closeSwapRfs(tileId, rfsId, dispatch);

    if (isDefined(reject.QuoteReject)) {
      // is it the end of the RFS window ?
      if (reject.QuoteReject.ErrorCode === 'EM_TIMEOUT') {
        // it is not an error. the RFS is just over
        return;
      }

      console.warn('RFS Quote aborted:', reject.QuoteReject.ErrorMessage);

      dispatch(
        swapErrorOccurred({
          tileId,
          errorType: 'rate',
          errorCode: reject.QuoteReject.ErrorCode,
          errorMessage: reject.QuoteReject.ErrorMessage,
        }),
      );
    }
  };
}
